import { useCallback, useContext } from 'react';
import Promise from 'bluebird';
import _ from 'underscore';
import { loadCampaign, loadBundleCountries, readAdGroup, readScheduleCriterions, readAdGroupScheduleCriterions, modelToViewModelForAdGroupSetting, saveAdGroup, readTargetCriterion, readAdGroupTargetCriterion, readLocationCriterions, readAdGroupLocationCriterions } from '@bingads-webui-campaign-react/search-campaign-data';
import { ScopeContext } from '@bingads-webui-react/scope-service';
import { ServerContext } from '@bingads-webui-campaign-react/server-context';
import { I18nContext } from '@bingads-webui-react/i18n-context';
import { ActivityContext, ScenarioContext } from '@bingads-webui-react/hoc-with-signal';
import { useWizardService, WIZARD_EVENTS } from '@bingads-webui-react/wizard';
import exclusionRepository from '@bingads-webui-campaign-legacy/advanced-settings/util/exclusion-repository';
import { ScopeLevel } from '@bingads-webui-campaign-react/constants';
import { entitySeparator, formatInvalidEntities } from '@bingads-webui-react/web-exclusions';
import { PermissionsContext } from '@bingads-webui-react/hoc-with-permissions';
import { globalizationModelSetup } from '@bingads-webui-campaign/ads-globalization';

export const useAdGroupEditSettingsRepository = () => {
  const { scope } = useContext(ScopeContext) || {};
  const scenario = useContext(ScenarioContext);
  const activity = useContext(ActivityContext);
  const serverContext = useContext(ServerContext);
  const {
    CurrentLocale: locale,
  } = serverContext;
  const permissions = useContext(PermissionsContext);
  const { state, send } = useWizardService();
  const {
    cid, aid, campaignId, adGroupId,
  } = scope.values;
  const i18n = useContext(I18nContext);
  const load = useCallback(() => {
    const loadPromises = {
      campaign: loadCampaign(cid, aid, campaignId),
      adGroup: readAdGroup(cid, aid, campaignId, adGroupId, i18n),
      campaignScheduleCriterion: readScheduleCriterions(cid, aid, campaignId),
      adGroupScheduleCriterion: readAdGroupScheduleCriterions(cid, aid, campaignId, adGroupId),
      exclusions: exclusionRepository.load(campaignId, adGroupId, ScopeLevel.AdGroup),
      campaignTargetCriterion: readTargetCriterion(cid, aid, campaignId),
      adGroupTargetCriterion: readAdGroupTargetCriterion(cid, aid, campaignId, adGroupId),
      campaignLocationTargets: readLocationCriterions(cid, aid, campaignId),
      adGroupLocationTargets: readAdGroupLocationCriterions(cid, aid, campaignId, adGroupId),
    };

    return Promise.props(loadPromises)
      .then(async ({
        campaign: rawCampaign,
        adGroup: rawAdGroup,
        campaignScheduleCriterion,
        adGroupScheduleCriterion,
        exclusions: [campaignExclusions, adgroupExclusions] = [null, null],
        campaignTargetCriterion,
        adGroupTargetCriterion,
        campaignLocationTargets,
        adGroupLocationTargets,
      }) => {
        const bundleCountriesAndLocationCriterions = await loadBundleCountries({
          cid, aid, serverContext, language: rawCampaign.Languages,
        });

        const { campaignViewModel, adGroupViewModel } = modelToViewModelForAdGroupSetting({
          adGroupModel: rawAdGroup,
          campaignModel: rawCampaign,
          campaignScheduleCriterionModel: campaignScheduleCriterion ? campaignScheduleCriterion.value : null,
          adGroupScheduleCriterionModel: adGroupScheduleCriterion ? adGroupScheduleCriterion.value : null,
          campaignTargetCriterionModel: campaignTargetCriterion ? campaignTargetCriterion.value : null,
          adGroupTargetCriterionModel: adGroupTargetCriterion ? adGroupTargetCriterion.value : null,
          bundleCountries: bundleCountriesAndLocationCriterions.bundleCountries,
          adGroupLocationTargets: adGroupLocationTargets ? adGroupLocationTargets.value : [],
          campaignLocationTargets: campaignLocationTargets ? campaignLocationTargets.value : [],
          i18n,
        });

        if (permissions && permissions.IsDomesticSimplifiedChineseEnabled) {
          const { LANGUAGES: supportedLanguages } = globalizationModelSetup({ permissions, culture: locale });
          campaignViewModel.Languages = _.chain(campaignViewModel.Languages)
            .filter(language => _.contains(supportedLanguages, language))
            .value();
          if (!_.contains(supportedLanguages, adGroupViewModel.Language)) {
            [adGroupViewModel.Language] = supportedLanguages;
          }
        }

        send(WIZARD_EVENTS.UPDATE_WIZARD_DATA, { exclusions: adgroupExclusions });
        return Promise.resolve({
          campaign: campaignViewModel,
          adGroup: {
            ...adGroupViewModel,
            allowAdGroupTypeChange: false,
          },
          ...bundleCountriesAndLocationCriterions,
          exclusions: {
            campaignExclusions,
            adgroupExclusions,
          },
        });
      })
      .catch((error) => {
        // eslint-disable-next-line no-console
        console.error(`Load adGroup settings failed: ${error}`);
        activity.signal(`onAdGroupSettingsLoadFailed - ${JSON.stringify({
          campaignId,
          adGroupId,
          error,
        })}`);
        return Promise.reject(error);
      });
  }, [activity, adGroupId, aid, campaignId, cid, i18n, send, serverContext, permissions, locale]);

  const save = useCallback(({ data }) => {
    const {
      adGroup,
      campaign,
      exclusions,
      bundleCountries,
      bundleLocationCriterions,
    } = data;


    return saveAdGroup({
      adGroup,
      campaign,
      bundleCountries,
      bundleLocationCriterions,
    }, {
      permissions,
      scenario: scenario.name,
      i18n,
      activityLogger: activity,
    })
      .then(() => {
        const {
          exclusions: savedExclusions,
        } = state.context.wizardData;
        const excludedSites = exclusions.adgroupExclusions.ExcludedSites.replace(entitySeparator, '');
        const isExclusionsChanged = !_.isEqual(excludedSites, savedExclusions.ExcludedSites);
        if (isExclusionsChanged) {
          const saveExclusionsData = {
            AdGroupId: adGroupId,
            CampaignId: campaignId,
            AdvancedSettings: {
              ExcludedSites: excludedSites,
            },
          };
          return exclusionRepository.saveExtensions(saveExclusionsData, ScopeLevel.AdGroup);
        }
        return {};
      })
      .catch((error) => {
        const {
          serverErrors = [],
          systemErrors = [],
        } = error || {};

        let exclusionsAfterSave = exclusions;
        // exclusion error
        if (error.NegativeSitesResult) {
          serverErrors.push({
            property: 'webExclusions',
            NegativeSitesResult: error.NegativeSitesResult.EntityErrors,
          });
          exclusionsAfterSave = {
            ...exclusionsAfterSave,
            adgroupExclusions: {
              ExcludedSites: formatInvalidEntities(error.NegativeSitesResult),
            },
          };
        }

        // eslint-disable-next-line no-console
        console.error(`Save adGroup settings failed: ${error}`);
        activity.signal(`onAdGroupSettingsSaveFailed - ${JSON.stringify({
          data,
          error,
        })}`);

        // eslint-disable-next-line prefer-promise-reject-errors
        return Promise.reject({
          data: {
            adGroup: error.adGroup || adGroup,
            campaign,
            exclusions: exclusionsAfterSave,
          },
          error: {
            serverErrors: _.flatten(serverErrors.filter(val => val !== null)),
            systemErrors: _.flatten(systemErrors.filter(val => val !== null)),
          },
        });
      });
  }, [activity, adGroupId, campaignId, i18n, scenario.name, state.context.wizardData, permissions]);

  return {
    load,
    save,
  };
};
